import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import useAuth from 'hooks/useAuth'
import { PAGE_NEW_SAP_BARCODE } from 'utils/constants'
import { fetchNewSapBarcodeUnseenCount } from 'utils/api/newSapBarcodeApi'
import { checkPageAccess } from 'utils/helpers'

type Props = {
  children: React.ReactNode
}

type NewSapBarcodeUnseenCountContextType = {
  /** New SAP barcodes not yet marked as seen. 0 when unavailable. */
  unseenCount: number
  /**
   * Re-reads the count. Call it after marking entries as seen so the badge
   * drops immediately instead of waiting for a reload.
   */
  refreshUnseenCount: () => Promise<void>
}

export const NewSapBarcodeUnseenCountContext = createContext<
  undefined | NewSapBarcodeUnseenCountContextType
>(undefined)

/**
 * Holds the unseen new-SAP-barcode count so the sidebar badge and the listing
 * share one value. Kept out of the listing component because the badge has to
 * survive navigating away from that page.
 */
const NewSapBarcodeUnseenCountProvider: React.FC<Props> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()

  const [unseenCount, setUnseenCount] = useState<number>(0)

  // The endpoint 401s without view access to the page, so only ask for the
  // count when this user can actually open the listing.
  const hasPageAccess = useMemo(
    () =>
      Boolean(checkPageAccess(PAGE_NEW_SAP_BARCODE.id, user?.pageAccess).page),
    [user?.pageAccess],
  )

  const refreshUnseenCount = useCallback(async () => {
    if (!isAuthenticated || !hasPageAccess) {
      setUnseenCount(0)
      return
    }

    try {
      const { data } = await fetchNewSapBarcodeUnseenCount()
      setUnseenCount(data?.data?.unseenCount || 0)
    } catch (error) {
      // The badge is decoration — a failed count should never toast or block.
      setUnseenCount(0)
    }
  }, [isAuthenticated, hasPageAccess])

  // Paints the badge on shell load. `hasPageAccess` is a boolean, so the
  // re-verify AuthProvider runs on every route change doesn't refetch this.
  useEffect(() => {
    refreshUnseenCount()
  }, [refreshUnseenCount])

  const value: NewSapBarcodeUnseenCountContextType = useMemo(
    () => ({ unseenCount, refreshUnseenCount }),
    [unseenCount, refreshUnseenCount],
  )

  return (
    <NewSapBarcodeUnseenCountContext.Provider value={value}>
      {children}
    </NewSapBarcodeUnseenCountContext.Provider>
  )
}

export default NewSapBarcodeUnseenCountProvider
